import React, { useMemo, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

import { colors } from '../theme';
import { tableTextLabels } from '../../../components/tableTextLabels';

import { CONSENT_SOURCES } from '../../../constants/whatsapp-contacts';
import ConsentChip from './ConsentChip';
import { matchesSearch } from '../../../components/searchMatch';

const useStyles = makeStyles((theme) => ({
  head: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    flexGrow: 1,
    minWidth: '260px',
    marginRight: theme.spacing(2),
  },
  intro: {
    display: 'block',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  tableWrap: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  sourceField: {
    minWidth: '220px',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  evidenceField: {
    flexGrow: 1,
    minWidth: '280px',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  evidenceHint: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  markButton: {
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

// Контакты, которым нельзя писать, пока согласие не зафиксировано.
// Менеджер собирает его в договоре или анкете и отмечает здесь.
function ConsentQueue(props) {
  const classes = useStyles();
  const { contacts, onMarkConsent, onShowInfo } = props;

  const [search, setSearch] = useState('');
  const [source, setSource] = useState(CONSENT_SOURCES[0]);
  const [evidence, setEvidence] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const selectedSet = useMemo(() => {
    const map = {};
    selectedIds.forEach((id) => {
      map[id] = true;
    });
    return map;
  }, [selectedIds]);

  const visible = useMemo(() => {
    if (!search.trim()) return contacts;
    // Телефон ищется по цифрам: с пробелами и без.
    return contacts.filter((c) => matchesSearch(search, [c.name, c.company, c.type, c.region], [c.phone]));
  }, [contacts, search]);

  const selectedIndices = useMemo(() => {
    const indices = [];
    visible.forEach((contact, index) => {
      if (selectedSet[contact.id]) {
        indices.push(index);
      }
    });
    return indices;
  }, [visible, selectedSet]);

  const tableData = useMemo(
    () =>
      visible.map((contact) => ({
        id: contact.id,
        name: contact.name,
        company: contact.company,
        phone: contact.phone,
        type: contact.type,
        region: contact.region,
        status: contact.consentStatus,
      })),
    [visible]
  );

  const columns = [
    // id не виден: по нему статус открывает «Информацию о подтверждении» нужного контакта.
    { name: 'id', options: { display: 'excluded', filter: false, viewColumns: false } },
    { name: 'name', label: 'Директор', options: { filter: false } },
    { name: 'company', label: 'Компания', options: { filter: false } },
    {
      name: 'phone',
      label: 'Телефон',
      options: {
        filter: false,
        setCellProps: () => ({ style: { whiteSpace: 'nowrap' } }),
      },
    },
    { name: 'type', label: 'Тип компании' },
    { name: 'region', label: 'Область' },
    {
      name: 'status',
      label: 'Согласие',
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => (
          <ConsentChip
            status={value}
            onClick={onShowInfo ? () => onShowInfo(tableMeta.rowData[0]) : undefined}
          />
        ),
      },
    },
  ];

  const options = {
    filterType: 'checkbox',
    selectableRows: 'multiple',
    selectableRowsHeader: true,
    rowsSelected: selectedIndices,
    onRowSelectionChange: (current, allSelected) => {
      setSelectedIds(allSelected.map((row) => visible[row.dataIndex].id));
    },
    customToolbarSelect: () => null,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100],
    sortOrder: { name: 'name', direction: 'asc' },
    textLabels: tableTextLabels,
  };

  // The tick marks and the typed document stay until the server has really saved.
  const handleMark = async () => {
    const saved = await onMarkConsent(selectedIds, source, evidence.trim());
    if (saved) {
      setSelectedIds([]);
      setEvidence('');
    }
  };

  if (contacts.length === 0) {
    return (
      <Grid className={classes.empty}>
        <CheckCircleIcon style={{ color: colors.green, fontSize: '40px' }} />
        <Typography variant="body1">
          Согласие есть у всей базы — собирать нечего.
        </Typography>
      </Grid>
    );
  }

  return (
    <div>
      <Typography variant="caption" className={classes.intro}>
        Этим контактам нельзя отправлять рассылку, пока согласие не
        зафиксировано. Самый простой путь: отправьте им приветственную рассылку
        с кнопками «Так / Ні» (раздел «Рассылки», шаблон с кнопками
        подтверждения): ответ клиента запишется сам. Если согласие получено
        иначе (договор, анкета, звонок), отметьте контакты здесь.
      </Typography>

      <div className={classes.head}>
        <FormControl className={classes.searchField}>
          <TextField
            label="Поиск"
            variant="outlined"
            placeholder="Имя, компания, телефон, область"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="disabled" />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </div>

      <div className={classes.tableWrap}>
        <MUIDataTable
          title="Согласие не получено"
          data={tableData}
          columns={columns}
          options={options}
        />
      </div>

      <div className={classes.actions}>
        <FormControl className={classes.sourceField}>
          <TextField
            select
            label="Основание согласия"
            variant="outlined"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            {CONSENT_SOURCES.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl className={classes.evidenceField}>
          <TextField
            label="Номер договора / ссылка на скан"
            variant="outlined"
            placeholder="Договор №2026-117 от 12.08.2026"
            value={evidence}
            onChange={(event) => setEvidence(event.target.value)}
          />
        </FormControl>
        <Button
          variant="contained"
          disableElevation
          className={classes.markButton}
          disabled={selectedIds.length === 0}
          startIcon={<CheckCircleIcon />}
          onClick={handleMark}
        >
          Отметить согласие ({selectedIds.length})
        </Button>
      </div>
      <Typography variant="caption" className={classes.evidenceHint}>
        Без артефакта журнал согласий не является доказательством: при жалобе
        нужно показать, чем именно подтверждается согласие.
      </Typography>
    </div>
  );
}

export default ConsentQueue;
